import React, { useEffect, useRef, useState } from 'react';
import PictureImage from '../PictureImage/PictureImage';

const VIDEO_A = '/assets/video/cinzia-model.mp4';
const VIDEO_B = '/assets/video/cinzia.mp4';
const POSTER = '/assets/video/cinzia-poster.jpg';

const POSTER_HOLD_MS = 3000;

/**
 * Left side — CINZIA ARAIA world.
 *
 * Cinematic entry pacing: a static B/W poster overlay covers the video
 * for the first 3 seconds, then fades out revealing the (already
 * playing) video. This applies uniformly on desktop AND mobile per
 * client spec.
 *
 * The overlay is a separate <PictureImage> above the videos rather than the
 * native `poster` attribute so we have full control over the fade-out
 * and never risk the poster disappearing before we want it to. The
 * videos themselves keep `autoPlay muted playsInline` (unchanged from
 * before) so browsers grant playback permission normally — no regression
 * in autoplay behaviour.
 *
 * Two videos crossfade back and forth with a 3s pause between each
 * cycle for a cinematic "surprise" beat. Videos are NOT `loop`ed so the
 * `ended` event fires; the current clip freezes on its last frame for
 * 3000ms, then we swap to the other video.
 *
 * Reduced motion: if `prefers-reduced-motion: reduce`, the poster
 * overlay stays visible indefinitely (no fade) — user preference to
 * avoid motion is respected.
 *
 * `speed` prop drives the active video's playbackRate for hover-respiro.
 */
export default function CinziaSide({ speed = 1 }) {
    const videoARef = useRef(null);
    const videoBRef = useRef(null);
    const [active, setActive] = useState('A'); // 'A' | 'B'
    const [posterVisible, setPosterVisible] = useState(true);
    // iter88 · Opzione C — Video stays opacity 0 until it has actually
    // started playing. This prevents Chrome from picking the <video>
    // element as LCP candidate (opacity 0 elements are skipped) until
    // AFTER the sneaker WebP + poster JPG have painted and won LCP.
    const [videoStarted, setVideoStarted] = useState(false);
    const pauseTimerRef = useRef(null);
    const posterTimerRef = useRef(null);

    useEffect(() => {
        const a = videoARef.current;
        const b = videoBRef.current;
        if (!a || !b) return;

        // iter88 · Perf FASE B · Fix 1 (P0 LCP root cause).
        //
        // PRIMA: qui c'era `v.preload = 'auto'` che sovrascriveva
        // `preload="metadata"` del JSX, forzando Chrome a scaricare
        // ~10.68 MiB di video PRIMA del primo frame → LCP 5.1s
        // simulated e fino a 136.7s su Slow4G reale (GSC).
        //
        // ORA: manteniamo `preload="metadata"` (già nel JSX), Chrome
        // scarica solo header/moov (~200-500KB per video) sufficiente
        // per autoplay muted. Il crossfade A↔B resta invariato perché
        // il video B viene precaricato dai metadata e il buffer
        // completa on-demand appena il primo termina.
        [a, b].forEach((v) => {
            v.muted = true;
            v.playsInline = true;
        });

        // Respect reduced-motion: keep the poster overlay visible forever.
        const prefersReducedMotion =
            typeof window !== 'undefined' &&
            window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const play = (v) => {
            // With preload="none" (iter88 Opzione C), the browser has
            // NOT fetched anything yet. Call load() explicitly so play()
            // has metadata to work with (Chrome does this implicitly on
            // play() too, but being explicit avoids race conditions).
            try { v.load(); } catch (_) { /* noop */ }
            const p = v.play();
            if (p && typeof p.catch === 'function') {
                p.catch(() => {
                    setTimeout(() => {
                        const p2 = v.play();
                        if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
                    }, 250);
                });
            }
        };

        // iter88 · Opzione C — On first `playing` event, mark the
        // video as "started" so CSS reveals it (opacity 0→1). Until
        // then, Chrome LCP algorithm ignores the <video> (opacity 0
        // elements are excluded) → sneaker WebP + poster JPG win LCP.
        const onPlaying = () => setVideoStarted(true);
        a.addEventListener('playing', onPlaying, { once: true });
        b.addEventListener('playing', onPlaying, { once: true });

        // iter88 · Perf FASE B · Opzione C — Defer autoplay ALIGNED
        // WITH LCP paint.
        //
        // PRIMA: `autoPlay` attribute + `preload="metadata"` sui <video>
        // = Chrome scaricava ~10.5 MiB di video anche con `preload=metadata`
        // per soddisfare l'autoplay policy → LCP = video Cinzia (~6.8s
        // Lighthouse mobile simulated).
        //
        // ORA: `autoPlay` rimosso dal JSX + `preload="none"` (Chrome
        // NON fetcha nulla del video). Chiamiamo `.play()` (che
        // triggera `load()`) DOPO che il primo LCP paint è avvenuto
        // (sneaker WebP fetchpriority=high o poster overlay), tramite
        // PerformanceObserver LCP + 200ms grace. Fallback safety net
        // 2000ms totali in caso PerformanceObserver non fira.
        //
        // Effetto: la sneaker WebP + poster JPG paintano PRIMA che il
        // video inizi a scaricare → LCP element = sneaker o poster
        // (piccoli, veloci) invece del video (~5MB). LCP scende
        // drasticamente su throttled slow4G reale.
        let started = false;
        const kick = () => {
            if (started) return;
            started = true;
            play(a);
        };
        const safetyTimer = setTimeout(kick, 2000);
        let lcpObserver = null;
        try {
            if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
                lcpObserver = new PerformanceObserver(() => {
                    // On first LCP entry (poster JPG or sneaker WebP
                    // paint), wait 200ms grace then start video.
                    setTimeout(() => {
                        clearTimeout(safetyTimer);
                        kick();
                    }, 200);
                    try { lcpObserver.disconnect(); } catch (_) { /* noop */ }
                });
                lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
            }
        } catch (_) { /* fallback to safetyTimer */ }

        // Poster overlay hold: 3s, then fade out.
        if (!prefersReducedMotion) {
            posterTimerRef.current = setTimeout(() => {
                setPosterVisible(false);
            }, POSTER_HOLD_MS);
        }

        // 3-second "held frame" between cycles.
        const scheduleSwap = (nextVideo, nextKey) => {
            clearTimeout(pauseTimerRef.current);
            pauseTimerRef.current = setTimeout(() => {
                nextVideo.currentTime = 0;
                play(nextVideo);
                setActive(nextKey);
            }, 3000);
        };
        const onEndedA = () => scheduleSwap(b, 'B');
        const onEndedB = () => scheduleSwap(a, 'A');

        a.addEventListener('ended', onEndedA);
        b.addEventListener('ended', onEndedB);
        // iter88 · Opzione C — rimosso `loadedmetadata`→play(a) perché
        // scattava appena arrivavano i metadata (~200-500KB) bypassando
        // il defer via requestIdleCallback. Il play iniziale è ora
        // gestito solo da `kick()` sopra.

        // Kick play on the first user interaction — last-resort fallback
        // for browsers that block programmatic autoplay.
        const kickOnce = () => {
            if (a.paused && b.paused) play(a);
            document.removeEventListener('touchstart', kickOnce);
            document.removeEventListener('click', kickOnce);
        };
        document.addEventListener('touchstart', kickOnce, { once: true, passive: true });
        document.addEventListener('click', kickOnce, { once: true });

        return () => {
            clearTimeout(posterTimerRef.current);
            clearTimeout(pauseTimerRef.current);
            clearTimeout(safetyTimer);
            try { if (lcpObserver) lcpObserver.disconnect(); } catch (_) { /* noop */ }
            a.removeEventListener('ended', onEndedA);
            b.removeEventListener('ended', onEndedB);
            a.removeEventListener('playing', onPlaying);
            b.removeEventListener('playing', onPlaying);
            document.removeEventListener('touchstart', kickOnce);
            document.removeEventListener('click', kickOnce);
        };
    }, []);

    useEffect(() => {
        const v = active === 'A' ? videoARef.current : videoBRef.current;
        if (!v) return;
        try {
            v.playbackRate = speed;
        } catch (e) {
            /* noop */
        }
    }, [speed, active]);

    return (
        <div className={`side-media side-media--cinzia ${videoStarted ? 'has-video' : ''}`} data-testid="cinzia-media">
            <video
                ref={videoARef}
                src={VIDEO_A}
                poster="/assets/video/cinzia-poster.jpg"
                width={1080}
                height={1920}
                muted
                playsInline
                preload="none"
                className={`cinzia-video ${active === 'A' ? 'is-active' : ''}`}
                data-testid="cinzia-video-a"
            />
            <video
                ref={videoBRef}
                src={VIDEO_B}
                poster="/assets/video/cinzia-poster.jpg"
                width={1080}
                height={1920}
                muted
                playsInline
                preload="none"
                className={`cinzia-video ${active === 'B' ? 'is-active' : ''}`}
                data-testid="cinzia-video-b"
            />
            {/* Poster overlay — covers the video for the first 3s then
                fades out. Sits above videos but below the dark overlay so
                the same colour treatment applies. */}
            <img
                src={POSTER}
                alt=""
                aria-hidden="true"
                className={`cinzia-poster-overlay ${posterVisible ? 'is-visible' : ''}`}
                data-testid="cinzia-poster-overlay"
                draggable={false}
            />
            <div className="side-overlay side-overlay--dark" />
            <img
                className="cinzia-side-mark"
                src="/logos/cinzia/mark-white.png"
                alt="Cinzia Araia"
                loading="lazy"
                draggable={false}
                data-testid="cinzia-side-mark"
            />
        </div>
    );
}
